import React from 'react';
import type { ApprovalPrintSigner } from '@/services/approval.service';
import type {
  PagePlan,
  PlannedPage,
  PlannedRowSegment,
  QuotationPrintItemBase,
  QuotationPrintModel,
} from '@/print-engine/types';
import {
  buildQuotationNoteChunks,
  buildQuotationTermsAndConditionsChunks,
} from '@/print-engine/quotation-print-model';
import { PRINT_ROW_ATTR, PRINT_SECTION_ATTR, PRINT_THEAD_ATTR } from '@/print-engine/dom-measure';
import { getCompanyLetterhead } from '@/print-engine/company-letterhead';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';
import { resolvePublicAssetUrl } from '@/config/apiBaseUrl';
import { richTextToPrintHtml } from '@/utils/richText';
import './PaginatedQuotationPrint.css';
import { quotationPrintShowsItemCode } from '@/print-engine/quotation-print-model';

function formatAmount(value: unknown) {
  return formatMoney(value ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);
}

function sectionAttr(id: string) {
  return { [PRINT_SECTION_ATTR]: id };
}

function rowAttr(seg: PlannedRowSegment) {
  return { [PRINT_ROW_ATTR]: `${seg.tableIndex}:${seg.itemIndex}` };
}

const THEAD_ATTR = { [PRINT_THEAD_ATTR]: '' };

function DescriptionBody({
  item,
  segment,
}: {
  item: QuotationPrintItemBase;
  segment: PlannedRowSegment;
}) {
  const chunks = item.descriptionChunks.slice(segment.chunkStart, segment.chunkEnd);
  return (
    <div className="qtn-desc-body">
      {segment.isContinuation ? (
        <div className="qtn-row-continued">Item {item.lineNumber} — Continued</div>
      ) : null}
      {chunks.map((chunk, i) => (
        <div key={`${segment.chunkStart}-${i}`} className="item-description">
          {chunk}
        </div>
      ))}
      {segment.showCommercial && item.specifications ? (
        <div className="item-specs">{item.specifications}</div>
      ) : null}
      {segment.showCommercial && item.notes ? <div className="item-notes">{item.notes}</div> : null}
      {segment.showCommercial && item.rentalCharges ? (
        <div className="item-notes">{item.rentalCharges}</div>
      ) : null}
      {segment.showCommercial && item.rentalBreakdownLines?.length ? (
        <div className="qtn-rental-breakdown">
          {item.rentalBreakdownLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function itemCodeCell(item: QuotationPrintItemBase, continuation: boolean, manufacturing: boolean): string {
  if (continuation) return '';
  if (item.itemCode.trim()) return item.itemCode;
  return manufacturing ? '—' : '';
}

function TradingRow({
  item,
  segment,
  model,
  showItemCode,
}: {
  item: QuotationPrintItemBase;
  segment: PlannedRowSegment;
  model: QuotationPrintModel;
  showItemCode: boolean;
}) {
  const refOnly = segment.isContinuation && !segment.showCommercial;
  return (
    <tr className={segment.isContinuation ? 'qtn-row-cont' : undefined} {...rowAttr(segment)}>
      <td className="sno">{segment.isContinuation ? '' : item.lineNumber}</td>
      {showItemCode ? (
        <td className="item-code-col">{itemCodeCell(item, segment.isContinuation, model.manufacturingColumns)}</td>
      ) : null}
      {model.manufacturingColumns ? (
        <td className="qty-sm">{segment.isContinuation ? '' : item.revision || '—'}</td>
      ) : null}
      <td className="desc-col">
        <DescriptionBody item={item} segment={segment} />
      </td>
      {!model.manufacturingColumns ? (
        <td className="qty-sm">{segment.isContinuation ? '' : item.revision || '—'}</td>
      ) : null}
      <td className="qty">{segment.showCommercial ? item.quantityLabel : refOnly ? '' : ''}</td>
      {model.technicalMode ? (
        <td className="qty-sm">{segment.showCommercial ? item.deliveryLabel || '—' : ''}</td>
      ) : null}
      {model.internalMode ? (
        <>
          <td className="num">{segment.showCommercial ? formatAmount(item.baseCost) : ''}</td>
          <td className="num">{segment.showCommercial ? formatAmount(item.allocatedOh) : ''}</td>
          <td className="num">{segment.showCommercial ? formatAmount(item.loadedCost) : ''}</td>
          <td className="num">
            {segment.showCommercial ? `${formatAmount(item.marginPct)}%` : ''}
          </td>
        </>
      ) : null}
      {!model.technicalMode ? (
        <>
          <td className="num">{segment.showCommercial ? formatAmount(item.unitPrice) : ''}</td>
          <td className="num">{segment.showCommercial ? formatAmount(item.netAmount) : ''}</td>
        </>
      ) : null}
    </tr>
  );
}

function RentalRow({
  item,
  segment,
  hideDates,
  showItemCode,
  technicalMode,
}: {
  item: QuotationPrintItemBase;
  segment: PlannedRowSegment;
  hideDates?: boolean;
  showItemCode: boolean;
  technicalMode?: boolean;
}) {
  return (
    <tr className={segment.isContinuation ? 'qtn-row-cont' : undefined} {...rowAttr(segment)}>
      <td className="sno">{segment.isContinuation ? '' : item.lineNumber}</td>
      {showItemCode ? (
        <td className="item-code-col">{itemCodeCell(item, segment.isContinuation, false)}</td>
      ) : null}
      <td className="desc-cell">
        <DescriptionBody item={item} segment={segment} />
      </td>
      <td className="qty-sm">{segment.showCommercial ? item.quantityLabel : ''}</td>
      <td className="uom-sm">{segment.showCommercial ? item.uom || '' : ''}</td>
      <td className="period-sm">{segment.showCommercial ? item.period || '' : ''}</td>
      <td className="dur-sm">{segment.showCommercial ? item.durationLabel || '' : ''}</td>
      {!hideDates ? (
        <td className="dates-sm">{segment.showCommercial ? item.datesText || '' : ''}</td>
      ) : null}
      {!technicalMode ? (
        <>
          <td className="num">{segment.showCommercial ? formatAmount(item.rate) : ''}</td>
          <td className="num">{segment.showCommercial ? formatAmount(item.netAmount) : ''}</td>
        </>
      ) : null}
    </tr>
  );
}

function ItemsTable({
  model,
  rows,
}: {
  model: QuotationPrintModel;
  rows: PlannedRowSegment[];
}) {
  const useRental =
    model.layout === 'rental_only' ||
    (model.layout === 'mixed' && rows.some((r) => model.items[r.itemIndex]?.kind === 'rental'));

  // Mixed pages: render one table; prefer trading columns if any trading segment present.
  const hasTrading = rows.some((r) => model.items[r.itemIndex]?.kind === 'trading');
  const rentalTable = useRental && !hasTrading;
  const showItemCode = quotationPrintShowsItemCode(model);

  if (rentalTable) {
    const hideDates = Boolean(model.rentalCompactCustomerPrint);
    return (
      <table
        className={`items-table qtn-items-table qtn-rental-items-table${
          model.technicalMode ? ' qtn-items-table-technical' : ''
        }`}
      >
        <thead {...THEAD_ATTR}>
          <tr>
            <th className="sno">S.No</th>
            {showItemCode ? <th className="item-code-head">Item Code</th> : null}
            <th className="desc-head">Description</th>
            <th className="qty-sm">Qty</th>
            <th className="uom-sm">UOM</th>
            <th className="period-sm">Period</th>
            <th className="dur-sm">Duration</th>
            {!hideDates ? <th className="dates-sm">Dates</th> : null}
            {!model.technicalMode ? (
              <>
                <th className="num">Rate ({model.totals.currency})</th>
                <th className="num">Net ({model.totals.currency})</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((seg) => {
            const item = model.items[seg.itemIndex];
            if (!item) return null;
            return (
              <RentalRow
                key={`${seg.itemIndex}-${seg.chunkStart}-${seg.chunkEnd}`}
                item={item}
                segment={seg}
                hideDates={hideDates}
                showItemCode={showItemCode}
                technicalMode={model.technicalMode}
              />
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <table
      className={[
        'items-table',
        'qtn-items-table',
        model.manufacturingColumns ? 'qtn-items-table-manufacturing' : '',
        model.internalMode ? 'qtn-items-table-internal' : '',
        model.technicalMode ? 'qtn-items-table-technical' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <thead {...THEAD_ATTR}>
        <tr>
          <th className="sno">S.No</th>
          {showItemCode ? <th className="item-code-head">Item Code</th> : null}
          {model.manufacturingColumns ? <th className="qty-sm">Rev</th> : null}
          <th className="desc-head">Description</th>
          {!model.manufacturingColumns ? <th className="qty-sm">Rev</th> : null}
          <th className="qty">{model.internalMode || model.technicalMode ? 'Qty' : 'Quantity'}</th>
          {model.technicalMode ? <th className="qty-sm">Delivery</th> : null}
          {model.internalMode ? (
            <>
              <th className="num">Base</th>
              <th className="num">OH</th>
              <th className="num">Loaded</th>
              <th className="num">Mgn %</th>
            </>
          ) : null}
          {!model.technicalMode ? (
            <>
              <th className="num">
                {model.internalMode
                  ? `Unit ${model.totals.currency}`
                  : `Unit Price ${model.totals.currency}`}
              </th>
              <th className="num">Total {model.totals.currency}</th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((seg) => {
          const item = model.items[seg.itemIndex];
          if (!item) return null;
          if (item.kind === 'rental') {
            // Fallback: show rental line inside trading-shaped table via description cell.
            return (
              <tr key={`${seg.itemIndex}-${seg.chunkStart}-${seg.chunkEnd}`} {...rowAttr(seg)}>
                <td className="sno">{seg.isContinuation ? '' : item.lineNumber}</td>
                {showItemCode ? (
                  <td className="item-code-col">{itemCodeCell(item, seg.isContinuation, model.manufacturingColumns)}</td>
                ) : null}
                {model.manufacturingColumns ? <td className="qty-sm">—</td> : null}
                <td className="desc-col" colSpan={model.manufacturingColumns ? 1 : 1}>
                  <DescriptionBody item={item} segment={seg} />
                </td>
                {!model.manufacturingColumns ? <td className="qty-sm">—</td> : null}
                <td className="qty">{seg.showCommercial ? item.quantityLabel : ''}</td>
                {model.technicalMode ? <td className="qty-sm">—</td> : null}
                {model.internalMode ? (
                  <>
                    <td className="num" />
                    <td className="num" />
                    <td className="num" />
                    <td className="num" />
                  </>
                ) : null}
                {!model.technicalMode ? (
                  <>
                    <td className="num">
                      {seg.showCommercial ? formatAmount(item.rate ?? item.unitPrice) : ''}
                    </td>
                    <td className="num">{seg.showCommercial ? formatAmount(item.netAmount) : ''}</td>
                  </>
                ) : null}
              </tr>
            );
          }
          return (
            <TradingRow
              key={`${seg.itemIndex}-${seg.chunkStart}-${seg.chunkEnd}`}
              item={item}
              segment={seg}
              model={model}
              showItemCode={showItemCode}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function SignaturesBlock({
  signers,
  showCustomerAcceptance = true,
}: {
  signers: ApprovalPrintSigner[];
  showCustomerAcceptance?: boolean;
}) {
  if (!signers.length && !showCustomerAcceptance) return null;
  return (
    <div className="qtn-signatures">
      {signers.length ? (
        <>
          <h4>Approvals</h4>
          <div className="qtn-signatures-grid">
            {signers.map((s) => {
              const sigSrc = resolvePublicAssetUrl(s.user.signature_image_url);
              return (
                <div key={`${s.step_number}-${s.user.id}`} className="qtn-signature-card">
                  {sigSrc ? (
                    <img src={sigSrc} alt="" className="qtn-signature-img" />
                  ) : (
                    <div className="qtn-signature-line" />
                  )}
                  <div className="qtn-signature-name">{s.user.full_name}</div>
                  {s.user.designation ? (
                    <div className="qtn-signature-role">{s.user.designation}</div>
                  ) : null}
                  {s.acted_at ? <div className="qtn-signature-date">{s.acted_at}</div> : null}
                </div>
              );
            })}
          </div>
        </>
      ) : null}
      {showCustomerAcceptance ? (
        <div className="qtn-acceptance">
          <h4>Customer Acceptance</h4>
          <div className="qtn-acceptance-fields">
            <div>Name: _______________________________</div>
            <div>Signature: __________________________</div>
            <div>Date: _______________________________</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RichTextChunk({ html, compact = false }: { html: string; compact?: boolean }) {
  const content = richTextToPrintHtml(html);
  if (!content) return null;
  return (
    <div
      className={`qtn-extra-terms qtn-rich-text${compact ? ' qtn-notes-compact' : ''}`}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

function ComposedPage({
  model,
  page,
  totalPages,
  diagnostics,
}: {
  model: QuotationPrintModel;
  page: PlannedPage;
  totalPages: number;
  diagnostics?: boolean;
}) {
  const showHeader = page.sections.includes('documentHeader');
  const showCustomer = page.sections.includes('customerBlock');
  const showIntro = page.sections.includes('intro');
  const showCont = page.sections.includes('continuationChrome');
  const showTable = page.sections.includes('tableHeader') || page.itemRows.length > 0;
  const showOverhead = !model.technicalMode && page.sections.includes('overhead');
  const showTotals = !model.technicalMode && page.sections.includes('totals');
  const showWords = !model.technicalMode && page.sections.includes('amountWords');
  const showTerms = page.sections.includes('terms');
  const noteChunkIndexes = page.sections
    .filter((s) => s.startsWith('notesChunk:'))
    .map((s) => Number(s.slice('notesChunk:'.length)))
    .filter((n) => Number.isFinite(n));
  const tcChunkIndexes = page.sections
    .filter((s) => s.startsWith('tcChunk:'))
    .map((s) => Number(s.slice('tcChunk:'.length)))
    .filter((n) => Number.isFinite(n));
  const noteChunks =
    model.noteChunks?.length > 0
      ? model.noteChunks
      : buildQuotationNoteChunks(model.notes, model.specialInstructions);
  const termsAndConditionsChunks =
    model.termsAndConditionsChunks?.length > 0
      ? model.termsAndConditionsChunks
      : buildQuotationTermsAndConditionsChunks(model.extraTerms);
  const pageNoteTexts = noteChunkIndexes
    .map((idx) => noteChunks[idx])
    .filter((t): t is string => Boolean(t && String(t).trim()));
  const pageTcTexts = tcChunkIndexes
    .map((idx) => termsAndConditionsChunks[idx])
    .filter((t): t is string => Boolean(t && String(t).trim()));
  const showNotes = pageNoteTexts.length > 0;
  const showTc = pageTcTexts.length > 0;
  const showSign = page.sections.includes('signatures');
  const notesContinued = showNotes && !showTerms && noteChunkIndexes[0] !== 0;
  const tcContinued = showTc && tcChunkIndexes[0] !== 0;

  return (
    <section
      className={`qtn-composed-page qtn-composed-page--${page.type.toLowerCase()}`}
      data-page={page.pageIndex + 1}
      data-page-type={page.type}
    >
      <img
        className="qtn-letterhead qtn-letterhead-header qtn-composed-letterhead-header"
        src={getCompanyLetterhead().headerSrc}
        alt=""
      />
      <div className="qtn-composed-body">
        {showCont ? (
          <div className="qtn-continuation-chrome">
            Quotation {model.quotationNumber} {model.revisionLabel || 'R0'} | {model.customerName} | Page{' '}
            {page.pageIndex + 1} of {totalPages}
          </div>
        ) : null}

        {showHeader ? (
          <div className="qtn-print-block" {...sectionAttr('documentHeader')}>
            <h1 className="qtn-title">{model.title}</h1>
            {model.subtitle ? <p className="qtn-subtitle">{model.subtitle}</p> : null}
          </div>
        ) : null}

        {showCustomer ? (
          <section className="qtn-meta" {...sectionAttr('customerBlock')}>
            <div className="qtn-customer">
              <table>
                <tbody>
                  <tr>
                    <th>M/S</th>
                    <td>{model.customerName}</td>
                  </tr>
                  {model.customerAddress ? (
                    <tr>
                      <th />
                      <td>{model.customerAddress}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <th>Attn</th>
                    <td>{model.attn}</td>
                  </tr>
                  {model.inquiryReference ? (
                    <tr>
                      <th>Inquiry Ref</th>
                      <td>{model.inquiryReference}</td>
                    </tr>
                  ) : null}
                  {model.sapNumber ? (
                    <tr>
                      <th>SAP#</th>
                      <td>{model.sapNumber}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <th>Subject</th>
                    <td>{model.subject}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="qtn-reference">
              <table>
                <tbody>
                  <tr>
                    <th>TRN</th>
                    <td>{model.trn}</td>
                  </tr>
                  <tr>
                    <th>Ref No</th>
                    <td>{model.quotationNumber}</td>
                  </tr>
                  <tr>
                    <th>Revision</th>
                    <td>{model.revisionLabel || 'R0'}</td>
                  </tr>
                  <tr>
                    <th>Date</th>
                    <td>{model.quotationDate}</td>
                  </tr>
                  {model.projectName ? (
                    <tr>
                      <th>Project</th>
                      <td>{model.projectName}</td>
                    </tr>
                  ) : null}
                  {model.opportunityNo ? (
                    <tr>
                      <th>Opportunity No</th>
                      <td>{model.opportunityNo}</td>
                    </tr>
                  ) : null}
                  {model.estimationNo ? (
                    <tr>
                      <th>Estimation No.</th>
                      <td>{model.estimationNo}</td>
                    </tr>
                  ) : null}
                  {model.manufacturer ? (
                    <tr>
                      <th>MFR</th>
                      <td>{model.manufacturer}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {showIntro ? (
          <div className="qtn-intro" {...sectionAttr('intro')}>
            <p>{model.introGreeting}</p>
            {model.introText.split('\n').map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        ) : null}

        {showTable ? <ItemsTable model={model} rows={page.itemRows} /> : null}

        {showOverhead ? (
          <div className="qtn-overhead-block" {...sectionAttr('overhead')}>
            {model.customerOverheadSummary ? <p>{model.customerOverheadSummary}</p> : null}
            {model.overheadLines.length > 0 ? (
              <table className="qtn-totals-table">
                <tbody>
                  {model.overheadLines.map((line) => (
                    <tr key={line.label}>
                      <td>{line.label}</td>
                      <td>{formatAmount(line.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        ) : null}

        {showTotals ? (
          <div className="qtn-totals-section" {...sectionAttr('totals')}>
            <table className="qtn-totals-table">
              <tbody>
                <tr>
                  <td>Subtotal :</td>
                  <td>{formatAmount(model.totals.subtotal)}</td>
                </tr>
                {model.totals.discount > 0 ? (
                  <tr>
                    <td>
                      {model.totals.discountPct > 0
                        ? `Discount (${model.totals.discountPct}%) :`
                        : 'Discount :'}
                    </td>
                    <td>- {formatAmount(model.totals.discount)}</td>
                  </tr>
                ) : null}
                {model.totals.discount > 0 ? (
                  <tr>
                    <td>Taxable Amt :</td>
                    <td>{formatAmount(model.totals.taxable)}</td>
                  </tr>
                ) : null}
                <tr>
                  <td>
                    {model.totals.vatApplicable && model.totals.vatPct > 0
                      ? `VAT Amount (${model.totals.vatPct}%):`
                      : 'VAT Amount :'}
                  </td>
                  <td>{formatAmount(model.totals.vat)}</td>
                </tr>
                <tr className="grand-total">
                  <td>Grand Total :</td>
                  <td>{formatAmount(model.totals.grand)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}

        {showWords ? (
          <div className="qtn-amount-words" {...sectionAttr('amountWords')}>
            <span>Total Only:</span>
            <strong>{model.totals.amountInWords}</strong>
          </div>
        ) : null}

        {showTerms ? (
          <div className="terms-section qtn-terms-section">
            <div className="qtn-print-block" {...sectionAttr('terms')}>
              <h4>{model.technicalMode ? 'Delivery / Validity' : 'Commercial Terms'}</h4>
              <table>
                <tbody>
                  {model.terms.map((t) => (
                    <tr key={t.key}>
                      <th>{t.key}</th>
                      <td>: {t.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {showNotes ? (
          <div className="terms-section qtn-terms-section qtn-notes-page">
            {pageNoteTexts.map((text, i) => (
              <div
                key={`notes-${noteChunkIndexes[i]}`}
                className="qtn-print-block"
                {...sectionAttr(`notesChunk:${noteChunkIndexes[i]}`)}
              >
                {i === 0 ? (
                  <h4 style={showTerms ? { marginTop: 6 } : undefined}>
                    {notesContinued ? 'Notes (continued)' : 'Notes'}
                  </h4>
                ) : null}
                <RichTextChunk html={text} compact />
              </div>
            ))}
          </div>
        ) : null}

        {showTc ? (
          <div className="terms-section qtn-terms-section qtn-tc-page">
            {pageTcTexts.map((text, i) => (
              <div
                key={`tc-${tcChunkIndexes[i]}`}
                className="qtn-print-block"
                {...sectionAttr(`tcChunk:${tcChunkIndexes[i]}`)}
              >
                {i === 0 ? <h4>{tcContinued ? 'Terms & Conditions (continued)' : 'Terms & Conditions'}</h4> : null}
                <RichTextChunk html={text} />
              </div>
            ))}
          </div>
        ) : null}

        {showSign ? (
          <div className="qtn-print-block" {...sectionAttr('signatures')}>
            <SignaturesBlock
              signers={model.signers}
              showCustomerAcceptance={!model.technicalMode}
            />
          </div>
        ) : null}

        <div className="qtn-composed-page-number">
          Page {page.pageIndex + 1} of {totalPages}
        </div>

        {diagnostics ? (
          <div className="qtn-print-diag no-print">
            {page.type} · used {Math.round(page.usedHeightPx)}px · unused{' '}
            {Math.round(page.unusedHeightPx)}px · rows {page.itemRows.length}
          </div>
        ) : null}
      </div>
      <img
        className="qtn-letterhead qtn-letterhead-footer qtn-composed-letterhead-footer"
        src={getCompanyLetterhead().footerSrc}
        alt=""
      />
    </section>
  );
}

export type PaginatedQuotationDocumentProps = {
  model: QuotationPrintModel;
  plan: PagePlan;
  diagnostics?: boolean;
};

export const PaginatedQuotationDocument: React.FC<PaginatedQuotationDocumentProps> = ({
  model,
  plan,
  diagnostics = false,
}) => (
  <div className="qtn-paginated-root printable-quotation quotation-qtn-format">
    {plan.pages.map((page) => (
      <ComposedPage
        key={page.pageIndex}
        model={model}
        page={page}
        totalPages={plan.totalPages}
        diagnostics={diagnostics}
      />
    ))}
  </div>
);
